import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { TicketList } from './TicketList';
import { ReplyEditor } from './ReplyEditor';
import useSupportTickets from '../../hooks/useSupportTickets';
import { useContextualHint } from '../../hooks/useContextualHint';
import ContextualHint from '../ContextualHint';

/**
 * SupportTicketsPage Component
 * Página de tickets de suporte para USUÁRIOS
 * 
 * Layout: Lista vertical com accordions que expandem para baixo
 * 
 * Funcionalidades do USUÁRIO:
 * - Ver seus próprios tickets
 * - Ver detalhes e respostas (expandindo o ticket)
 * - Responder aos tickets (como usuário)
 * - Criar novos tickets (via widget flutuante)
 */
export function SupportTicketsPage() {
  const supportTickets = useSupportTickets();
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  // Obter dica contextual (engine global)
  const hint = useContextualHint('support-tickets', {});

  const handleOpenSupportWidget = useCallback(() => {
    const widgetButton = document.querySelector('.agendario-support-widget-button');
    if (widgetButton && typeof widgetButton.click === 'function') {
      widgetButton.click();
      return;
    }

    console.warn('[SupportTicketsPage] Support widget button not found (.agendario-support-widget-button).');
  }, []);

  // Fetch user's tickets
  const handleFetchTickets = useCallback(async (pageNum = 1) => {
    await supportTickets.fetchTickets({
      page: pageNum,
      pageSize: supportTickets.pageSize,
    });
  }, [supportTickets]);

  // Handle ticket toggle (expand/collapse)
  const handleToggleTicket = useCallback(async (ticketId) => {
    if (ticketId === null) {
      // Recolhendo - limpar seleção
      setSelectedTicketId(null);
      return;
    }
    
    // Expandindo - carregar detalhes
    setSelectedTicketId(ticketId);
    await supportTickets.fetchTicketDetails(ticketId);
  }, [supportTickets]);

  // Handle reply submission (as USER, not admin)
  const handleSubmitReply = useCallback(async (replyData) => {
    if (!selectedTicketId) return;
    
    try {
      await supportTickets.submitReply(selectedTicketId, replyData.content);
      // Recarregar detalhes do ticket para mostrar a nova resposta
      await supportTickets.fetchTicketDetails(selectedTicketId);
    } catch (err) {
      console.error('Failed to submit reply:', err);
    }
  }, [selectedTicketId, supportTickets]);

  // Initial load
  useEffect(() => {
    handleFetchTickets(1);
  }, []);

  // Render reply editor for the expanded ticket
  const renderReplyEditor = useCallback(() => (
    <ReplyEditor
      ticketId={selectedTicketId}
      onSubmit={handleSubmitReply}
      loading={supportTickets.replySubmitting}
    />
  ), [selectedTicketId, handleSubmitReply, supportTickets.replySubmitting]);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Meus Tickets de Suporte
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Acompanhe seus tickets e envie mensagens para nossa equipe
          </Typography>
        </Box>

        <ContextualHint hint={hint} />

        {/* Actions */}
        <Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => handleFetchTickets(1)}
              disabled={supportTickets.listLoading}
            >
              Atualizar
            </Button>

            <Button variant="contained" onClick={handleOpenSupportWidget}>
              Novo Ticket
            </Button>
          </Stack>
        </Box>

        {/* Error */}
        {supportTickets.listError && (
          <Alert severity="error">{supportTickets.listError}</Alert>
        )}

        {supportTickets.replyError && (
          <Alert severity="error">{supportTickets.replyError}</Alert>
        )}

        {/* Ticket List - Layout vertical, expande para baixo */}
        <TicketList
          tickets={supportTickets.tickets}
          loading={supportTickets.listLoading}
          error={supportTickets.listError}
          totalCount={supportTickets.totalCount}
          page={supportTickets.page}
          pageSize={supportTickets.pageSize}
          onPageChange={(newPage) => handleFetchTickets(newPage)}
          selectedTicketId={selectedTicketId}
          onToggleTicket={handleToggleTicket}
          selectedTicket={supportTickets.selectedTicket}
          ticketDetailsLoading={supportTickets.ticketDetailsLoading}
          replies={supportTickets.replies}
          renderReplyEditor={renderReplyEditor}
        />
      </Stack>
    </Container>
  );
}

export default SupportTicketsPage;
